import type {StructuredPatch} from 'diff';

// In git diffs, /dev/null is used as a sentinel value to indicate added or
// deleted files. See <https://git-scm.com/docs/git-diff#_combined_diff_format>
// for more details. We use it here to color the pill and determine the correct
// label for added/deleted files.
const NULL_SENTINEL = '/dev/null';

/** Strips the `a/` or `b/` prefix git diff adds to filenames. */
function getPatchFileName(fileName: string) {
  return fileName.replace(/^[ab]\//, '');
}

/** Returns the canonical filename: oldFileName for deletions, newFileName otherwise. */
export function getFileName(patch: StructuredPatch) {
  if (isDeletedFile(patch)) {
    return getPatchFileName(patch.oldFileName);
  }
  return getPatchFileName(patch.newFileName);
}

/** Returns true if the patch represents a newly created file (`git add`). */
export function isAddedFile(patch: StructuredPatch) {
  return patch.oldFileName === NULL_SENTINEL;
}

/** Returns true if the patch represents a deleted file (`git rm`). */
export function isDeletedFile(patch: StructuredPatch) {
  return patch.newFileName === NULL_SENTINEL;
}

/** Returns true if the patch represents a rename or move (old and new names differ). */
export function isRenamedFile(patch: StructuredPatch) {
  return (
    !isAddedFile(patch) &&
    !isDeletedFile(patch) &&
    getOldFileName(patch) !== getNewFileName(patch)
  );
}

/** Returns the new filename with the `b/` prefix stripped. */
export function getNewFileName(patch: StructuredPatch) {
  return getPatchFileName(patch.newFileName);
}

/** Returns the old filename with the `a/` prefix stripped. */
export function getOldFileName(patch: StructuredPatch) {
  return getPatchFileName(patch.oldFileName);
}
